// src/pages/t-landing/common/helper.js

import { getTokenAndLocation, getTokenAndKey, copyText } from '@/utils';
import {
  createMallOrder,
  getSignResult,
  signedGoodsList,
  activeCard,
} from '@/api/tiktokSign';
import { CardService } from '@/service/card';
import { env } from '@hb/hbridge';
import { startPolling } from '@/utils/tools.js';
import { getVersion, getSystemCode } from '@/utils/systemCode';

/**
 * 商品列表数据适配
 * @param {Array} list - 原始商品列表
 * @returns {Array} 适配后的商品列表
 */
function goodsListAdp(list = []) {
  return list.map((item) => {
    const goodsList = [{
      skuId: item.skuId,
      spuId: item.spuId,
      num: 1,
      salePrice: item.salePrice,
    }];
    return {
      ...item,
      goodsList,
      icon: item.picUrl,
      title: item.goodsName,
      subtitle: item.shortRemark,
      originalPrice: item.originalPrice,
      buyButtonText: `仅${item.salePrice}元`,
    };
  });
}

/**
 * 创建订单
 * @param {Object} params - 订单参数
 * @param {number} params.orderType - 订单类型
 * @param {number} params.chargeType - 充值类型
 * @param {number} params.payAmount - 支付金额
 * @param {Array} params.itemList - 商品列表
 * @returns {Promise<Object>} 订单结果
 */
export async function createOrder(params) {
  const {
    token, cityCode, adCode, longitude: lon, latitude: lat,
  } = await getTokenAndLocation({ needLogin: true });

  const baseParams = {
    token,
    cityCode,
    adCode,
    lon,
    lat,
    ...params,
  };

  try {
    const { data: payOrderInfo, code, msg } = await createMallOrder(baseParams);
    if (code !== 0) {
      return {
        success: false,
        code,
        msg,
      };
    }
    return {
      success: true,
      payOrderInfo,
      code,
      msg,
    };
  } catch (error) {
    return {
      success: false,
      msg: error.message,
    };
  }
}

/**
 * 调用抖音签约并支付组件
 * @param {Object} params - 支付参数
 * @param {string} params.orderId - 订单ID
 * @param {string} params.goodsTag - 商品标签
 * @param {string} params.bizScene - 业务场景
 * @returns {Promise<Object>} 支付结果
 */
export async function callDouYinPayAndSign(params) {
  return new Promise((resolve) => {
    CardService.payTiktokMoney(params).then(() => {
      resolve({
        success: true,
      });
    }).catch(() => {
      resolve({
        success: false,
      });
    });
  });
}

/**
 * 查询签约结果
 * @param {Object} params - 查询参数
 * @param {string} params.planType - 计划类型
 * @param {number} params.timeout - 超时时间
 * @returns {Promise<Object>} 签约结果
 */
export async function queryDouYinSignResult(params) {
  const { token, userNewId } = await getTokenAndKey();
  return new Promise((resolve) => {
    const { planType, sceneType = '', ...others } = params;
    startPolling({
      requestFn: () => getSignResult({
        token,
        userNewId,
        planType,
        sceneType,
      }),
      ...others,
      callback: (res) => {
        if (res.end) {
          resolve({
            signIn: false,
          });
          return true;
        }
        if (res.code === 0 && res.data && res.data.signIn) {
          resolve({
            signIn: true,
          });
          return true;
        }
        return false;
      },
    });
  });
}

/**
 * 查询购卡后售卖商品列表
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 商品列表结果
 */
export async function querySignedGoodsList(params = {}) {
  try {
    const {
      cityCode,
      adCode,
      userNewId,
      guid,
      token,
    } = await getTokenAndLocation();

    const res = await signedGoodsList({
      token,
      cityCode,
      adCode,
      userNewId,
      _uuid: guid,
      version: await getVersion(),
      systemCode: getSystemCode(),
      pageNo: 1,
      pageSize: 11,
      ...params,
    });

    if (res.code === 0) {
      return {
        success: true,
        cardList: goodsListAdp(res.data),
      };
    }
    return {
      success: true,
      cardList: [],
    };
  } catch (error) {
    return {
      success: false,
      cardList: [],
    };
  }
}

/**
 * 复制哈口令并跳转到应用市场
 * @param {string} HaCode - 哈口令
 */
// eslint-disable-next-line import/prefer-default-export
export async function copyHaCodeAndJumpToAppMarket(HaCode) {
  if (HaCode) {
    copyText(HaCode);
  }
  if (env.isIOS) {
    window.location.href = 'https://apps.apple.com/cn/app/true/id1165227346';
  } else if (env.isAndroid) {
    window.location.href = ('market://details?id=com.jingyao.easybike');
  }
}

export async function activeCardHelper(params) {
  const { cityCode, userNewId, token } = await getTokenAndLocation();
  return new Promise((resolve) => {
    activeCard({
      token,
      cityCode,
      userNewId,
      ...params,
    }).then((activeRes) => {
      if (activeRes.code === 0) {
        resolve({
          success: true,
        });
      } else {
        resolve({
          success: false,
          message: activeRes.msg,
        });
      }
    }).catch((e) => {
      resolve({
        success: false,
        message: e.message,
      });
    });
  });
}
